import { HOME_ART } from "../assets";
import { Reveal } from "../Reveal";
import { SECTION_IDS, type MarketingCopy } from "../copy";
import { ClockIcon, TickIcon } from "../icons";
import { Section, SectionHeading } from "./primitives";

export interface HowItWorksProps {
  t: MarketingCopy;
}

/**
 * How a class actually uses it — the one thing a reader cannot guess from the
 * word "software": one laptop at the front, children on their feet.
 *
 * ⚠️ THE STEP NUMBERS CARRY THE STEP'S COLOUR AND A LIP, LIKE A KID BUTTON, but
 * they are not controls — they are `aria-hidden` and the list is an `<ol>`, so a
 * screen reader gets "1 of 3" from the markup rather than from a painted digit.
 */
const STEP_TONES = [
  "bg-sky shadow-[0_5px_0_color-mix(in_srgb,var(--color-sky)_60%,var(--color-ink))]",
  "bg-primary shadow-[0_5px_0_var(--color-primary-deep)]",
  "bg-leaf shadow-[0_5px_0_var(--color-kid-secondary-fg)]",
] as const;

export function HowItWorks({ t }: HowItWorksProps) {
  return (
    <Section id={SECTION_IDS.how} className="pt-24 lg:pt-32">
      <SectionHeading label={t.how.label} heading={t.how.heading} accentClass="bg-primary" labelClass="text-primary" />

      <div className="mt-12 grid items-center gap-12 lg:mt-14 lg:grid-cols-[1.08fr_1fr] lg:gap-16">
        <Reveal variant="left">
          <figure className="relative">
            <img
              src={HOME_ART.classroom}
              alt={t.how.imageAlt}
              width={1280}
              height={956}
              loading="lazy"
              decoding="async"
              className="aspect-[5/4] w-full rounded-2xl object-cover shadow-[0_30px_60px_-28px_color-mix(in_srgb,var(--color-ink)_45%,transparent)]"
            />
            <figcaption className="contents">
              <span className="absolute top-6 -left-3 flex items-center gap-3 rounded-xl bg-surface py-2.5 pr-4 pl-2.5 shadow-raised sm:-left-6">
                <span aria-hidden="true" className="flex h-9 w-9 items-center justify-center rounded-lg bg-leaf text-surface">
                  <TickIcon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-dash text-dash-sm font-extrabold text-ink">{t.how.chipMove.title}</span>
                  <span className="block font-dash text-dash-xs font-semibold text-muted">{t.how.chipMove.body}</span>
                </span>
              </span>
              <span className="absolute -right-3 bottom-8 flex items-center gap-3 rounded-xl bg-surface py-2.5 pr-4 pl-2.5 shadow-raised sm:-right-6">
                <span aria-hidden="true" className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal/12 text-teal">
                  <ClockIcon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-dash text-dash-sm font-extrabold text-ink">{t.how.chipSlot.title}</span>
                  <span className="block font-dash text-dash-xs font-semibold text-muted">{t.how.chipSlot.body}</span>
                </span>
              </span>
            </figcaption>
          </figure>
        </Reveal>

        <ol className="space-y-3.5">
          {t.how.steps.map((step, i) => (
            <li key={step.title}>
              <Reveal delay={i * 90} variant="right">
                <div className="flex gap-5 rounded-xl border border-line bg-surface p-5 transition-colors duration-200 ease-dash hover:border-primary/30 hover:bg-cream sm:p-6">
                  <span
                    aria-hidden="true"
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-pill font-kid text-kid-sm font-extrabold text-surface ${STEP_TONES[i] ?? ""}`}
                  >
                    {i + 1}
                  </span>
                  <span>
                    <span className="block font-dash text-dash-lg font-extrabold text-ink">{step.title}</span>
                    <span className="mt-1 block font-dash text-dash-sm text-ink-soft">{step.body}</span>
                  </span>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
}
